import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import { authAPI } from '../services/api';
import { Users, Loader2 } from 'lucide-react';

const HRLogin = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast({
        title: 'Error',
        description: 'Please enter email and password',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      const response = await authAPI.login({ email, password });

      /*
        Backend returns:
        {
          success,
          user: { email, fullName, role, companyId },
          token
        }
      */

      if (!response || !response.user || !response.token) {
        throw new Error('Invalid server response');
      }

      const { user, token } = response;

      // 🔒 Allow only Company HR
      if (user.role !== 'company_hr') {
        toast({
          title: 'Access Denied',
          description: 'This portal is for Company HR only.',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      // ✅ Store auth details
      localStorage.setItem('authToken', token);
      localStorage.setItem('userRole', user.role);
      localStorage.setItem('userEmail', user.email);
      localStorage.setItem('userFullName', user.fullName || '');
      localStorage.setItem('companyId', user.companyId || '');

      toast({
        title: 'Login Successful',
        description: `Welcome back, ${user.fullName || ''}!`,
      });

      navigate('/hr/dashboard');

    } catch (error) {
      console.error('HR Login error:', error);

      toast({
        title: 'Login Failed',
        description:
          error?.response?.data?.detail ||
          error.message ||
          'Invalid email or password',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 bg-white shadow-xl border-0">

        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl mx-auto flex items-center justify-center mb-4">
            <Users className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Company HR Portal
          </h1>
          <p className="text-gray-500 mt-2">
            Sign in to manage your company's hiring
          </p>
        </div>

        <form onSubmit={handleLogin} className="space-y-5">

          <div>
            <Label htmlFor="email" className="text-gray-700 font-medium">
              Email Address
            </Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="hr@company.com"
              required
              className="mt-1 bg-gray-50 border-gray-300 focus:border-purple-500 focus:ring-purple-500"
            />
          </div>

          <div>
            <Label htmlFor="password" className="text-gray-700 font-medium">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              className="mt-1 bg-gray-50 border-gray-300 focus:border-purple-500 focus:ring-purple-500"
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white py-2.5"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Signing In...
              </>
            ) : (
              'Sign In'
            )}
          </Button>
        </form>

        <div className="mt-6 pt-6 border-t border-gray-200 text-center">
          <p className="text-sm text-gray-500">
            Not an HR?{' '}
            <a
              href="/recruiter/login"
              className="text-purple-600 hover:text-purple-700 font-medium"
            >
              Recruiter Login
            </a>
          </p>
          <p className="text-sm text-gray-500 mt-2">
            <a
              href="/"
              className="text-purple-600 hover:text-purple-700 font-medium"
            >
              Back to Home
            </a>
          </p>
        </div>

      </Card>
    </div>
  );
};

export default HRLogin;